import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Cloud,
  Copy,
  Database,
  Download,
  FileText,
  Fingerprint,
  HardDrive,
  HeartPulse,
  Layers3,
  LockKeyhole,
  Network,
  PackageCheck,
  Search,
  Server,
  ShieldCheck,
  Warehouse,
} from 'lucide-react';
import SEO from '@/components/common/SEO';
import { MapleLeafMark } from '@/components/common/MapleLeafMark';
import Header from '@/components/layout/Header';
import MainLayout from '@/components/layout/MainLayout';
import CorporateFooter from '@/components/layout/CorporateFooter';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { buildFaqSchema } from '@/lib/seo';
import { cn } from '@/lib/utils';

const MCP_VERSION = '0.2.1';
const MCP_MANIFEST_URL = 'https://gateway.canxp.ai/downloads/mcp/latest.json';
const MCP_CHECKSUMS_URL = 'https://gateway.canxp.ai/downloads/mcp/SHA256SUMS';

type PlatformKey = 'linux-x64' | 'linux-arm64' | 'darwin-arm64' | 'darwin-x64' | 'win32-x64';
type SourceStatus = 'available' | 'preview' | 'planned';

const PLATFORM_LABELS: Record<PlatformKey, string> = {
  'darwin-arm64': 'macOS · Apple silicon',
  'darwin-x64': 'macOS · Intel',
  'linux-x64': 'Linux · x64',
  'linux-arm64': 'Linux · arm64',
  'win32-x64': 'Windows · x64',
};

const PLATFORM_SHORT_LABELS: Record<PlatformKey, string> = {
  'darwin-arm64': 'macOS',
  'darwin-x64': 'macOS',
  'linux-x64': 'Linux',
  'linux-arm64': 'Linux',
  'win32-x64': 'Windows',
};

async function detectPlatform(): Promise<PlatformKey> {
  if (typeof window === 'undefined') return 'linux-x64';
  const ua = `${navigator.userAgent} ${navigator.platform}`.toLowerCase();
  type UAData = { getHighEntropyValues?: (hints: string[]) => Promise<{ architecture?: string }> };
  const uaData = (navigator as Navigator & { userAgentData?: UAData }).userAgentData;
  let architecture: string | undefined;

  if (uaData?.getHighEntropyValues) {
    try {
      architecture = (await uaData.getHighEntropyValues(['architecture'])).architecture;
    } catch {
      // Platform detection is best-effort; every build remains visible in the download section.
    }
  }

  const explicitArm = architecture === 'arm' || /arm64|aarch64/.test(ua);
  const explicitX64 = architecture === 'x86' || /x86_64|x64|amd64/.test(ua);
  if (ua.includes('mac')) return explicitX64 ? 'darwin-x64' : 'darwin-arm64';
  if (ua.includes('linux') || ua.includes('android')) return explicitArm ? 'linux-arm64' : 'linux-x64';
  return 'win32-x64';
}

const SectionHeading = ({ eyebrow, title, description, align = 'center', inverse = false }: {
  eyebrow: string;
  title: string;
  description: string;
  align?: 'left' | 'center';
  inverse?: boolean;
}) => (
  <div className={cn('max-w-3xl', align === 'center' && 'mx-auto text-center')}>
    <p className={cn('mb-4 text-xs font-bold uppercase tracking-[0.24em]', inverse ? 'text-cyan-300' : 'text-sky-600 dark:text-sky-400')}>
      {eyebrow}
    </p>
    <h2 className={cn('text-3xl font-semibold tracking-[-0.04em] sm:text-4xl lg:text-5xl', inverse ? 'text-white' : 'text-foreground')}>
      {title}
    </h2>
    {description ? (
      <p className={cn('mt-5 text-base leading-8 sm:text-lg', inverse ? 'text-slate-300' : 'text-muted-foreground')}>
        {description}
      </p>
    ) : null}
  </div>
);

const CodeBlock = ({ code, caption, className }: { code: string; caption?: string; className?: string }) => {
  const [copied, setCopied] = React.useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // The code remains selectable if clipboard access is unavailable.
    }
  };

  return (
    <div className={cn('overflow-hidden rounded-xl border border-slate-200/10 bg-[#07111f] shadow-lg shadow-slate-950/10', className)}>
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-2.5">
        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{caption ?? 'canxp-mcp.yaml'}</span>
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] text-slate-400 transition-colors hover:bg-white/5 hover:text-white"
          aria-label="Copy code"
        >
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="overflow-x-auto p-4 font-mono text-[12px] leading-6 text-slate-200 sm:text-[13px]"><code>{code}</code></pre>
    </div>
  );
};

const StatusBadge = ({ status }: { status: SourceStatus }) => (
  <Badge
    variant="outline"
    className={cn(
      'rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em]',
      status === 'available'
        ? 'border-emerald-500/25 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
        : status === 'preview'
          ? 'border-amber-500/25 bg-amber-500/10 text-amber-700 dark:text-amber-300'
          : 'border-slate-400/25 bg-slate-500/5 text-slate-500',
    )}
  >
    <span
      className={cn(
        'mr-1.5 h-1.5 w-1.5 rounded-full',
        status === 'available' ? 'bg-emerald-500' : status === 'preview' ? 'bg-amber-500' : 'bg-slate-400',
      )}
    />
    {status}
  </Badge>
);

const sourceFamilies: Array<{
  status: SourceStatus;
  icon: React.ElementType;
  name: string;
  systems: string;
  body: string;
}> = [
  {
    status: 'available',
    icon: Database,
    name: 'Relational databases',
    systems: 'PostgreSQL · MySQL · MariaDB',
    body: 'Search and retrieve bounded results across only the tables you explicitly allowlist.',
  },
  {
    status: 'available',
    icon: FileText,
    name: 'Documents & file shares',
    systems: 'PDF · DOCX · Markdown · text · CSV',
    body: 'Search local or mounted storage without uploading the corpus to another service. PDF and DOCX are extracted to text on the way through.',
  },
  {
    status: 'available',
    icon: Cloud,
    name: 'Object storage',
    systems: 'Amazon S3 · Azure Blob · MinIO · R2',
    body: 'Search governed object stores from the infrastructure that already has access. Azure uses a read-only SAS token, scoped and expiring.',
  },
  {
    status: 'available',
    icon: HeartPulse,
    name: 'Healthcare',
    systems: 'FHIR R4 · Azure Health Data Services',
    body: 'Reach clinical systems through explicit, bounded, read-only source definitions. Only GET is ever issued.',
  },
  {
    status: 'preview',
    icon: Warehouse,
    name: 'Cloud warehouses',
    systems: 'Snowflake · BigQuery',
    body: 'Make curated warehouse tables answerable without exporting a new copy of the data. Built and tested against recorded API responses; not yet exercised against a paid account.',
  },
  {
    status: 'planned',
    icon: Layers3,
    name: 'More on the roadmap',
    systems: 'Azure SQL · Synapse · OCR',
    body: 'T-SQL engines and optical character recognition for scanned documents are next on the connector roadmap.',
  },
];

const securityPoints = [
  {
    icon: LockKeyhole,
    title: 'Credentials stay in your environment',
    body: 'Connection strings use references such as env:PG_DSN. They are never written into the config and never sent to CanXP.',
    proof: 'dsn: env:PG_DSN',
  },
  {
    icon: ShieldCheck,
    title: 'Read-only by construction',
    body: 'The interface exposes search, get, and list. The model cannot submit SQL, and there is no write method to disable.',
    proof: 'search · get · list',
  },
  {
    icon: Fingerprint,
    title: 'The allowlist is the boundary',
    body: 'A table that is not listed cannot be searched, read, or discovered. Wildcards are not accepted.',
    proof: 'tables: [patients, visits]',
  },
  {
    icon: PackageCheck,
    title: 'Bounded and verifiable',
    body: 'Result limits are capped by your configuration. Every binary ships with a SHA256 checksum so you can verify exactly what you run.',
    proof: 'limit: capped · SHA256',
  },
];

const setupSteps = [
  {
    number: '01',
    title: 'Describe the boundary',
    description: 'Name the source, reference the credential, and allowlist only the tables the agent may reach.',
    caption: 'canxp-mcp.yaml',
    code: `sources:
  - name: clinic
    type: postgres
    dsn: env:PG_DSN
    tables: [patients, visits]`,
  },
  {
    number: '02',
    title: 'Inspect before serving',
    description: 'Validate the file and see exactly what the server would expose before a client connects.',
    caption: 'terminal',
    code: `canxp-mcp --check

✓ source clinic connected
✓ 2 tables allowlisted
✓ configuration valid`,
  },
  {
    number: '03',
    title: 'Start the server',
    description: 'Run one binary over stdio. CanXP Research—or any MCP client—can now use the source.',
    caption: 'terminal',
    code: `canxp-mcp

CanXP MCP ready
transport: stdio
methods: search, get, list`,
  },
];

const mcpFaqs = [
  {
    question: 'How is this different from giving an AI database access?',
    answer: 'It is not general database access. CanXP MCP exposes a read-only window onto tables you nominate and returns bounded results. The model cannot write, cannot run SQL, and cannot see a table you did not list.',
  },
  {
    question: 'Does our data go to CanXP?',
    answer: 'No. The server runs on your infrastructure and connects directly to your database or files. The result becomes context for the question you asked, exactly like opening a file. Credentials are never transmitted to CanXP.',
  },
  {
    question: 'What is available today?',
    answer: 'PostgreSQL is available for search, fetch, and list across allowlisted tables. Markdown, text, CSV, and code are available on local or mounted storage. Snowflake, BigQuery, S3, Azure Blob, FHIR, HL7, MySQL, PDF, and DOCX support are planned.',
  },
  {
    question: 'What happens when a question would return too much data?',
    answer: 'Every source has a bounded result limit that is capped in configuration. A caller cannot raise it beyond that cap, so one broad question cannot pull a million rows.',
  },
  {
    question: 'Can our security team audit it?',
    answer: 'Yes. Published binaries include SHA256 checksums so your team can verify exactly what they run, and the codebase is deliberately small enough to review in an afternoon. Source access for security review is available under NDA — ask us.',
  },
  {
    question: 'What if we already operate MCP servers?',
    answer: 'Keep using them. CanXP Research works with any compatible MCP server. CanXP MCP exists for the more common case where you have a database or file share but no server yet.',
  },
  {
    question: 'How are database results ranked beside documents?',
    answer: 'CanXP Research uses reciprocal rank fusion to combine each source by position rather than pretending their scores are comparable. Every result retains provenance, and failed sources are reported rather than silently treated as empty.',
  },
];

const MCPGuide: React.FC = () => {
  const [downloads, setDownloads] = React.useState<Partial<Record<PlatformKey, string>>>({});
  const [detected, setDetected] = React.useState<PlatformKey>('linux-x64');
  const [version, setVersion] = React.useState(MCP_VERSION);

  React.useEffect(() => {
    let active = true;
    void detectPlatform().then((platform) => active && setDetected(platform));
    fetch(MCP_MANIFEST_URL, { cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : null))
      .then((data: { version?: string; downloads?: Partial<Record<PlatformKey, string>> } | null) => {
        if (!active || !data) return;
        if (data.version) setVersion(data.version);
        setDownloads(data.downloads ?? {});
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  const downloadHref = (key: PlatformKey) =>
    downloads[key] ??
    `https://gateway.canxp.ai/downloads/mcp/canxp-mcp-${version}-${key}.${key.startsWith('win32') ? 'zip' : 'tar.gz'}`;

  const scrollToSetup = () => document.getElementById('setup')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <MainLayout>
      <SEO
        title="CanXP MCP — Your data, searchable in one question"
        description="Turn PostgreSQL and file shares into read-only, allowlisted MCP sources with one config file and one cross-platform binary. Search every source together with provenance."
        keywords="CanXP MCP, Model Context Protocol, PostgreSQL MCP server, database AI connector, read-only MCP, hybrid search, Snowflake MCP, enterprise data search"
        url="/mcp"
        schema={buildFaqSchema(mcpFaqs)}
      />

      <div className="min-h-screen overflow-hidden bg-background text-foreground">
        <Header />
        <main>
          <section className="relative isolate overflow-hidden border-b bg-[#061321] text-white">
            <div className="absolute inset-0 -z-30 bg-[linear-gradient(135deg,#061321_0%,#08243b_50%,#0a3752_100%)]" />
            <div className="absolute -right-40 -top-56 -z-20 h-[700px] w-[700px] rounded-full bg-cyan-300/20 blur-[130px]" />
            <div className="absolute -bottom-80 -left-56 -z-20 h-[720px] w-[720px] rounded-full bg-sky-500/20 blur-[150px]" />
            <div className="absolute inset-0 -z-10 opacity-30 [background-image:radial-gradient(circle_at_center,rgba(255,255,255,.22)_1px,transparent_1px)] [background-size:32px_32px] [mask-image:linear-gradient(to_bottom,black,transparent_88%)]" />

            <div className="container mx-auto grid min-h-[720px] gap-14 px-4 py-20 sm:py-24 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:py-28">
              <div>
                <div className="mb-6 flex items-center gap-3">
                  <MapleLeafMark size={40} className="text-[#2db0d5]" />
                  <span className="text-sm font-semibold uppercase tracking-[0.18em] text-sky-200/80">CanXP MCP</span>
                </div>
                <div className="inline-flex items-center gap-2 rounded-full border border-cyan-200/20 bg-cyan-100/10 px-3 py-1.5 text-xs font-medium text-cyan-100">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  Databases, documents, object storage and FHIR available today
                </div>
                <h1 className="mt-7 max-w-3xl text-5xl font-semibold leading-[0.95] tracking-[-0.055em] sm:text-6xl lg:text-7xl">
                  Your data,
                  <span className="block bg-gradient-to-r from-cyan-200 via-sky-300 to-blue-300 bg-clip-text text-transparent">wherever it lives.</span>
                </h1>
                <p className="mt-7 max-w-2xl text-lg leading-8 text-slate-300 sm:text-xl">
                  Turn a database or file share into an answerable source with one config file and one binary. No integration project. No credentials sent to CanXP.
                </p>
                <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                  <Button asChild size="lg" className="h-12 rounded-lg bg-white px-6 text-base text-slate-950 hover:bg-cyan-50">
                    <a href={downloadHref(detected)} download>
                      <Download className="mr-2 h-4 w-4" />
                      Download for {PLATFORM_SHORT_LABELS[detected]}
                    </a>
                  </Button>
                  <Button
                    type="button"
                    size="lg"
                    variant="outline"
                    className="h-12 rounded-lg border-white/20 bg-white/[0.04] px-6 text-base text-white hover:bg-white/10 hover:text-white"
                    onClick={scrollToSetup}
                  >
                    See the 60-second setup <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
                <div className="mt-7 flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-400">
                  {['Read-only interface', 'Explicit table allowlist', 'Five native builds', 'Verified checksums'].map((item) => (
                    <span key={item} className="inline-flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-cyan-300" />{item}</span>
                  ))}
                </div>
              </div>

              <div className="relative mx-auto w-full max-w-2xl lg:pl-10">
                <div className="absolute left-1/2 top-1/2 -z-10 h-80 w-80 -translate-x-1/2 -translate-y-1/2 rounded-full bg-cyan-300/20 blur-[100px]" />
                <div className="relative min-h-[470px] rounded-[2rem] border border-white/10 bg-white/[0.045] p-5 shadow-2xl shadow-slate-950/40 backdrop-blur-sm sm:p-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-200">CanXP data cloud</p>
                      <p className="mt-1 text-sm text-slate-400">One query · every allowed source</p>
                    </div>
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-200/20 bg-cyan-100/10">
                      <Network className="h-5 w-5 text-cyan-200" />
                    </div>
                  </div>

                  <div className="relative mt-9 grid grid-cols-[1fr_auto_1fr] items-center gap-3 sm:gap-5">
                    <div className="space-y-3">
                      {[
                        [Database, 'PostgreSQL', 'ready'],
                        [FileText, 'File share', 'ready'],
                        [Warehouse, 'Warehouse', 'planned'],
                      ].map(([Icon, label, status]) => {
                        const ItemIcon = Icon as React.ElementType;
                        return (
                          <div key={label as string} className="rounded-xl border border-white/10 bg-[#071724]/80 p-3.5">
                            <div className="flex items-center gap-3">
                              <ItemIcon className="h-4 w-4 text-cyan-300" />
                              <span className="text-xs font-medium text-slate-200">{label as string}</span>
                            </div>
                            <p className={cn('mt-2 text-[9px] font-bold uppercase tracking-wider', status === 'ready' ? 'text-emerald-400' : 'text-slate-600')}>{status as string}</p>
                          </div>
                        );
                      })}
                    </div>

                    <div className="flex flex-col items-center">
                      <span className="h-14 w-px bg-gradient-to-b from-transparent to-cyan-300/50" />
                      <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-cyan-200/30 bg-cyan-100/15 shadow-[0_0_40px_rgba(103,232,249,.18)]">
                        <Search className="h-7 w-7 text-cyan-100" />
                      </div>
                      <span className="h-14 w-px bg-gradient-to-b from-cyan-300/50 to-transparent" />
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-[#071724]/90 p-4">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Ranked answer</span>
                        <span className="h-2 w-2 rounded-full bg-emerald-400" />
                      </div>
                      <div className="mt-4 space-y-3">
                        {[92, 78, 62, 46].map((width, index) => (
                          <div key={width}>
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-[9px] text-cyan-300">0{index + 1}</span>
                              <div className="h-1.5 rounded-full bg-slate-600" style={{ width: `${width}%` }} />
                            </div>
                            <p className="ml-6 mt-1 text-[9px] text-slate-600">[{index % 2 === 0 ? 'postgres' : 'files'}#{index + 1}]</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 rounded-xl border border-white/10 bg-[#050d16] p-4 font-mono text-xs text-slate-300">
                    <span className="text-cyan-300">❯</span> Which active patients have an incomplete intake?
                    <div className="mt-3 flex items-center gap-2 text-[10px] text-slate-500">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> PostgreSQL + documents searched in parallel
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="border-b bg-slate-50 dark:bg-slate-950/30">
            <div className="container mx-auto grid grid-cols-2 divide-x divide-y border-x sm:grid-cols-4 sm:divide-y-0">
              {[
                ['1', 'configuration file'],
                ['3', 'read-only methods'],
                ['0', 'runtime dependencies'],
                ['5', 'native platform builds'],
              ].map(([value, label]) => (
                <div key={label} className="px-4 py-7 text-center">
                  <div className="text-3xl font-semibold tracking-tight text-sky-700 dark:text-cyan-300">{value}</div>
                  <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
                </div>
              ))}
            </div>
          </section>

          <section id="setup" className="scroll-mt-20 py-20 sm:py-28">
            <div className="container mx-auto px-4">
              <SectionHeading
                eyebrow="From database to answer in 60 seconds"
                title="No SDK. No connector project."
                description="Before: weeks of integration work before the first question. After: a ten-line file and a binary that runs where your data already is."
              />
              <div className="mt-14 grid gap-5 lg:grid-cols-3">
                {setupSteps.map((step) => (
                  <article key={step.number} className="relative rounded-2xl border bg-card p-5 shadow-sm sm:p-6">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs font-bold text-sky-600 dark:text-cyan-300">{step.number}</span>
                      <span className="h-px w-14 bg-gradient-to-r from-sky-300 to-transparent" />
                    </div>
                    <h3 className="mt-5 text-xl font-semibold tracking-tight">{step.title}</h3>
                    <p className="mt-3 min-h-[72px] text-sm leading-6 text-muted-foreground">{step.description}</p>
                    <CodeBlock code={step.code} caption={step.caption} className="mt-5" />
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="border-y bg-slate-50 py-20 dark:bg-slate-950/30 sm:py-28">
            <div className="container mx-auto px-4">
              <div className="grid gap-12 lg:grid-cols-[0.75fr_1.25fr] lg:items-end">
                <SectionHeading
                  eyebrow="Connector roadmap"
                  title="All your major data sources, one boundary."
                  description="Relational databases, documents, object storage and FHIR all ship today. Warehouses are in preview. One configuration model, and one allowlist, across every one of them."
                  align="left"
                />
                <p className="rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm leading-6 text-sky-900 dark:border-cyan-500/20 dark:bg-cyan-500/5 dark:text-cyan-100">
                  Every card says exactly what is available now. Planned connectors are shown as roadmap—not as features you can use today.
                </p>
              </div>

              <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border bg-border md:grid-cols-2 xl:grid-cols-3">
                {sourceFamilies.map((source) => {
                  const Icon = source.icon;
                  return (
                    <article key={source.name} className="bg-background p-6 transition-colors hover:bg-sky-50/50 dark:hover:bg-sky-950/10">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-100 text-sky-700 dark:bg-cyan-400/10 dark:text-cyan-300">
                          <Icon className="h-5 w-5" />
                        </div>
                        <StatusBadge status={source.status} />
                      </div>
                      <h3 className="mt-6 text-xl font-semibold tracking-tight">{source.name}</h3>
                      <p className="mt-1 font-mono text-[11px] text-sky-700 dark:text-cyan-400">{source.systems}</p>
                      <p className="mt-4 text-sm leading-7 text-muted-foreground">{source.body}</p>
                    </article>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="relative overflow-hidden bg-[#061321] py-20 text-white sm:py-28">
            <div className="absolute -right-40 top-0 h-96 w-96 rounded-full bg-cyan-300/10 blur-[120px]" />
            <div className="container relative mx-auto px-4">
              <div className="grid gap-14 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
                <div>
                  <SectionHeading
                    eyebrow="Hybrid search"
                    title="One question. Every source answers together."
                    description="CanXP Research searches files, a local vector index, notes, and every configured MCP source in parallel—then uses reciprocal rank fusion to produce one honest ranking."
                    align="left"
                    inverse
                  />
                  <div className="mt-8 space-y-3 text-sm text-slate-300">
                    {[
                      'Provenance stays attached to every result.',
                      'Scores from incompatible systems are never compared directly.',
                      'A failed source is reported, not quietly treated as empty.',
                    ].map((item) => (
                      <div key={item} className="flex items-start gap-3"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" />{item}</div>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 sm:p-7">
                  <div className="rounded-xl bg-[#050d16] p-5 font-mono text-xs leading-6 text-slate-300 sm:text-sm">
                    <p className="text-slate-500">research_search "Where do we handle patient consent?"</p>
                    <div className="mt-6 grid grid-cols-[1fr_auto] gap-x-4 gap-y-2">
                      <span><span className="text-cyan-300">index</span> · local semantic search</span><span className="text-emerald-400">4 hits</span>
                      <span><span className="text-cyan-300">files</span> · workspace text search</span><span className="text-emerald-400">11 hits</span>
                      <span><span className="text-cyan-300">vault</span> · Obsidian notes</span><span className="text-slate-500">empty</span>
                      <span><span className="text-cyan-300">mcp</span> · clinic PostgreSQL</span><span className="text-emerald-400">7 hits</span>
                    </div>
                    <div className="my-5 h-px bg-white/10" />
                    <p className="text-white">1. src/consent.ts <span className="text-cyan-300">[files#1, index#2]</span></p>
                    <p className="mt-2 text-white">2. patient_consent:8472 <span className="text-cyan-300">[mcp#1]</span></p>
                    <p className="mt-2 text-white">3. docs/policy.md <span className="text-cyan-300">[index#1]</span></p>
                  </div>
                  <div className="mt-4 grid gap-3 sm:grid-cols-3">
                    {['Parallel retrieval', 'Rank-level fusion', 'Source provenance'].map((item) => (
                      <div key={item} className="rounded-lg border border-white/10 bg-white/[0.03] p-3 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400">{item}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-20 sm:py-28">
            <div className="container mx-auto px-4">
              <SectionHeading
                eyebrow="A security boundary you can inspect"
                title="Not a promise. A deliberately small interface."
                description="The product does not ask you to trust a broad claim. Each security property maps to a configuration value, an interface constraint, or code your team can read."
              />
              <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border bg-border md:grid-cols-2 xl:grid-cols-4">
                {securityPoints.map((point) => {
                  const Icon = point.icon;
                  return (
                    <article key={point.title} className="bg-background p-6">
                      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-100 text-sky-700 dark:bg-cyan-400/10 dark:text-cyan-300">
                        <Icon className="h-5 w-5" />
                      </div>
                      <h3 className="mt-6 text-lg font-semibold tracking-tight">{point.title}</h3>
                      <p className="mt-3 text-sm leading-7 text-muted-foreground">{point.body}</p>
                      <code className="mt-5 block rounded-lg bg-muted px-3 py-2 text-[11px] text-foreground">{point.proof}</code>
                    </article>
                  );
                })}
              </div>
              <div className="mt-6 flex flex-col items-start justify-between gap-4 rounded-2xl border border-sky-200 bg-sky-50 p-6 dark:border-cyan-500/20 dark:bg-cyan-500/5 sm:flex-row sm:items-center">
                <div>
                  <p className="font-semibold text-sky-950 dark:text-cyan-100">Software that holds your database credentials should be short enough to read.</p>
                  <p className="mt-1 text-sm text-sky-800/70 dark:text-cyan-200/60">Every build ships with SHA256 checksums, and we make the source available for security review under NDA.</p>
                </div>
                <Button asChild variant="outline" className="border-sky-300 bg-white text-sky-900 hover:bg-sky-100 dark:border-cyan-500/30 dark:bg-transparent dark:text-cyan-100 dark:hover:bg-cyan-500/10">
                  <Link to="/enterprise">Request a source review<ArrowRight className="ml-2 h-3.5 w-3.5" /></Link>
                </Button>
              </div>
            </div>
          </section>

          <section id="download" className="scroll-mt-20 border-y bg-slate-50 py-20 dark:bg-slate-950/30 sm:py-28">
            <div className="container mx-auto max-w-6xl px-4">
              <div className="grid gap-12 lg:grid-cols-[0.75fr_1.25fr] lg:items-start">
                <div>
                  <SectionHeading
                    eyebrow={`Download · version ${version}`}
                    title="Run it where your data already is."
                    description="One native binary, no runtime. Put it on the machine or network that already has access to the source."
                    align="left"
                  />
                  <Button asChild size="lg" className="mt-8 h-12 bg-sky-700 px-6 text-white hover:bg-sky-800 dark:bg-cyan-400 dark:text-slate-950 dark:hover:bg-cyan-300">
                    <a href={downloadHref(detected)} download><Download className="mr-2 h-4 w-4" />Download for {PLATFORM_SHORT_LABELS[detected]}</a>
                  </Button>
                  <div className="mt-5 flex flex-wrap gap-4 text-xs text-muted-foreground">
                    <a href={MCP_CHECKSUMS_URL} className="inline-flex items-center gap-1.5 hover:text-foreground"><Fingerprint className="h-3.5 w-3.5" />SHA256SUMS</a>
                  </div>
                </div>

                <div className="overflow-hidden rounded-2xl border bg-background shadow-sm">
                  {(Object.keys(PLATFORM_LABELS) as PlatformKey[]).map((key, index) => (
                    <a
                      key={key}
                      href={downloadHref(key)}
                      download
                      className={cn('flex items-center justify-between gap-4 p-5 transition-colors hover:bg-sky-50 dark:hover:bg-cyan-500/5', index !== 0 && 'border-t')}
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-100 text-sky-700 dark:bg-cyan-400/10 dark:text-cyan-300">
                          <HardDrive className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold">{PLATFORM_LABELS[key]}</p>
                          <p className="mt-1 text-xs text-muted-foreground">Native binary · no runtime</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {key === detected ? <Badge variant="secondary" className="text-[10px]">Detected</Badge> : null}
                        <Download className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="py-20 sm:py-28">
            <div className="container mx-auto px-4">
              <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
                <div>
                  <SectionHeading
                    eyebrow="Bring your own server"
                    title="Already speak MCP? Keep what works."
                    description="CanXP Research is an MCP client. Connect an existing local or remote server, nominate the read tool, and include it in the same ranked search as every other source."
                    align="left"
                  />
                  <div className="mt-8 space-y-3">
                    {[
                      ['Register', 'canxp-research mcp add'],
                      ['Authenticate', 'canxp-research mcp auth <name>'],
                      ['Diagnose', 'canxp-research mcp debug <name>'],
                    ].map(([label, command]) => (
                      <div key={label} className="flex items-center justify-between gap-4 rounded-xl border p-4">
                        <span className="text-sm font-semibold">{label}</span>
                        <code className="text-xs text-muted-foreground">{command}</code>
                      </div>
                    ))}
                  </div>
                </div>
                <CodeBlock
                  caption="~/.config/canxp/canxp.jsonc"
                  code={`{
  "research_search": {
    "sources": {
      "index": true,
      "files": true,
      "vault": true
    },
    "mcp": [{
      "server": "warehouse",
      "tool": "search_encounters",
      "queryArg": "query"
    }],
    "timeoutMs": 8000
  }
}`}
                />
              </div>
            </div>
          </section>

          <section className="border-y bg-slate-50 py-20 dark:bg-slate-950/30 sm:py-28">
            <div className="container mx-auto max-w-4xl px-4">
              <SectionHeading eyebrow="Questions" title="The details security and data teams ask first." description="Direct answers about boundaries, deployment, roadmap, and verification." />
              <Accordion type="single" collapsible className="mt-10 rounded-2xl border bg-background px-5">
                {mcpFaqs.map((faq, index) => (
                  <AccordionItem key={faq.question} value={`faq-${index}`}>
                    <AccordionTrigger className="text-left text-base font-semibold hover:no-underline">{faq.question}</AccordionTrigger>
                    <AccordionContent className="text-sm leading-7 text-muted-foreground sm:text-base">{faq.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>

          <section className="relative overflow-hidden bg-[#061321] px-4 py-20 text-white sm:py-28">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_130%,rgba(34,211,238,.2),transparent_50%)]" />
            <div className="container relative mx-auto max-w-4xl text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-cyan-200/20 bg-cyan-100/10"><Server className="h-6 w-6 text-cyan-200" /></div>
              <h2 className="mt-7 text-4xl font-semibold tracking-[-0.045em] sm:text-6xl">The first question should not take a quarter.</h2>
              <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-slate-300">Download one binary, define the boundary in one file, and make the data you already have answerable.</p>
              <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
                <Button asChild size="lg" className="h-12 bg-white px-7 text-slate-950 hover:bg-cyan-50">
                  <a href={downloadHref(detected)} download><Download className="mr-2 h-4 w-4" />Download CanXP MCP</a>
                </Button>
                <Button asChild size="lg" variant="outline" className="h-12 border-white/20 bg-white/[0.04] px-7 text-white hover:bg-white/10 hover:text-white">
                  <Link to="/research">Explore CanXP Research<ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
              </div>
            </div>
          </section>
        </main>
        <CorporateFooter />
      </div>
    </MainLayout>
  );
};

export default MCPGuide;
